import { useReducer, useCallback, useEffect, useRef } from 'react';
import { Notification } from '../types/notification';
import { useAuth } from '../contexts/AuthContext';
import * as notificationsApi from '../api/notifications';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
}

type NotificationAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: Notification[] }
  | { type: 'FETCH_ERROR'; payload: string }
  | { type: 'MARK_READ'; payload: string }
  | { type: 'MARK_ALL_READ' }
  | { type: 'ADD_NOTIFICATION'; payload: Notification };

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
};

function notificationReducer(state: NotificationState, action: NotificationAction): NotificationState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null };
    case 'FETCH_SUCCESS': {
      const notifications = action.payload;
      return {
        ...state,
        notifications,
        unreadCount: notifications.filter((n) => !n.read).length,
        loading: false,
        error: null,
      };
    }
    case 'FETCH_ERROR':
      return { ...state, loading: false, error: action.payload };
    case 'MARK_READ': {
      const notifications = state.notifications.map((n) =>
        n.id === action.payload ? { ...n, read: true } : n
      );
      return {
        ...state,
        notifications,
        unreadCount: notifications.filter((n) => !n.read).length,
      };
    }
    case 'MARK_ALL_READ': {
      const notifications = state.notifications.map((n) => ({ ...n, read: true }));
      return { ...state, notifications, unreadCount: 0 };
    }
    case 'ADD_NOTIFICATION': {
      const notifications = [action.payload, ...state.notifications];
      return {
        ...state,
        notifications,
        unreadCount: notifications.filter((n) => !n.read).length,
      };
    }
    default:
      return state;
  }
}

export function useNotifications() {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const { user } = useAuth();
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchNotifications = useCallback(async () => {
    if (!user?._id) return;
    dispatch({ type: 'FETCH_START' });
    try {
      const notifications = await notificationsApi.getNotifications(user._id);
      dispatch({ type: 'FETCH_SUCCESS', payload: notifications });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch notifications';
      dispatch({ type: 'FETCH_ERROR', payload: message });
    }
  }, [user?._id]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await notificationsApi.markNotificationAsRead(notificationId);
      dispatch({ type: 'MARK_READ', payload: notificationId });
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!user?._id) return;
    try {
      await notificationsApi.markAllNotificationsAsRead(user._id);
      dispatch({ type: 'MARK_ALL_READ' });
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
    }
  }, [user?._id]);

  // Poll for new notifications every 60 seconds
  useEffect(() => {
    if (!user?._id) return;

    fetchNotifications();

    pollIntervalRef.current = setInterval(fetchNotifications, 60000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [user?._id, fetchNotifications]);

  return {
    notifications: state.notifications,
    unreadCount: state.unreadCount,
    loading: state.loading,
    error: state.error,
    markAsRead,
    markAllAsRead,
    fetchNotifications,
  };
}
